import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Section } from "./Common";
import BRAND from "../constants/brand";
import { BookOpen, Quote, ShoppingBag, Sparkles, ArrowRight } from "lucide-react";

/**
 * DeepContentBlocks — opt-in deep editorial sections for any hub page.
 *
 * Drop into any static hub page (Men's, Women's, Families, etc.) to render
 * the Phase 6.3 "deep content architecture":
 *   - Editorial body sections
 *   - Quotes & Mantras
 *   - Recommended products
 *   - Ask Donny premium block
 *
 * All props are optional; pass only what's relevant for that page.
 */
export default function DeepContentBlocks({
  pageTitle,
  editorial = [],
  quotes = [],
  products = [],
  askDonnyPrompt,
  testidPrefix = "deep",
}) {
  const { t } = useTranslation();
  const askDonnyHref = askDonnyPrompt
    ? `/ask-donny?prompt=${encodeURIComponent(askDonnyPrompt)}`
    : "/ask-donny";

  return (
    <>
      {/* Editorial */}
      {editorial.length > 0 && (
        <Section eyebrow={t("ecosystem.editorial_eyebrow")} title={t("ecosystem.editorial_title")}>
          <div className="grid md:grid-cols-2 gap-5" data-testid={`${testidPrefix}-editorial`}>
            {editorial.map((e, i) => (
              <article key={i} className="card-surface p-6 md:p-7" data-testid={`${testidPrefix}-editorial-${i}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#E2C77A]/25 to-[#7A5F2A]/10 border border-[#C8A24A]/30 grid place-items-center shrink-0">
                    <BookOpen className="w-4 h-4 gold-text" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl leading-tight">{e.title}</h3>
                </div>
                <div className="prose-coach text-sm md:text-[15px] text-white/80 leading-relaxed whitespace-pre-line">
                  {e.body}
                </div>
              </article>
            ))}
          </div>
        </Section>
      )}

      {/* Quotes & Mantras */}
      {quotes.length > 0 && (
        <Section eyebrow={t("ecosystem.quotes_eyebrow")} title={t("ecosystem.quotes_title")}>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testidPrefix}-quotes`}>
            {quotes.map((q, i) => (
              <figure key={i} className="card-surface p-6 relative overflow-hidden" data-testid={`${testidPrefix}-quote-${i}`}>
                <Quote className="absolute top-3 right-3 w-7 h-7 gold-text opacity-25" />
                <blockquote className="font-display text-lg md:text-xl leading-snug text-white/90">
                  &ldquo;{q.text}&rdquo;
                </blockquote>
                {q.author && (
                  <figcaption className="mt-3 text-xs tracking-[0.2em] uppercase gold-text">— {q.author}</figcaption>
                )}
              </figure>
            ))}
          </div>
        </Section>
      )}

      {/* Recommended Products */}
      {products.length > 0 && (
        <Section eyebrow={t("ecosystem.products_eyebrow")} title={t("ecosystem.products_recommended_for")}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testidPrefix}-products`}>
            {products.map((p, i) => (
              <Link
                key={i}
                to={p.to || "/shop"}
                className="card-surface p-5 group hover:border-[#C8A24A]/45 transition-colors flex flex-col"
                data-testid={`${testidPrefix}-product-${i}`}
              >
                <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/30 to-[#7A5F2A]/10 border border-[#C8A24A]/30 grid place-items-center mb-3">
                  <ShoppingBag className="w-4 h-4 gold-text" />
                </div>
                <div className="text-[10px] tracking-[0.28em] uppercase gold-text">{p.kind || t("ecosystem.program")}</div>
                <div className="font-display text-lg leading-tight mt-1">{p.title}</div>
                {p.summary && <p className="text-sm text-white/65 mt-2 leading-relaxed line-clamp-3">{p.summary}</p>}
                <div className="mt-3 text-xs gold-text inline-flex items-center gap-1 group-hover:underline">
                  {t("ecosystem.explore")} <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Ask Donny premium block */}
      <Section eyebrow={t("ecosystem.donny_eyebrow")} title={t("ecosystem.donny_about", { title: pageTitle || "" }).trim()}>
        <div
          className="card-surface p-6 md:p-8 grid md:grid-cols-[140px_1fr_auto] items-center gap-6"
          data-testid={`${testidPrefix}-donny-block`}
        >
          <img
            src={BRAND.DONNY_AVATAR}
            alt={t("ecosystem.donny_avatar_alt")}
            className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover ring-1 ring-[#C8A24A]/35 shadow-[0_0_40px_rgba(212,175,55,0.25)] mx-auto md:mx-0"
            loading="lazy"
          />
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-2 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> {t("ecosystem.donny_meta_chip")}
            </div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight">
              {t("ecosystem.donny_hl")} <span className="gold-text">{pageTitle}</span>.
            </h3>
            <p className="text-white/72 mt-2 max-w-[60ch] leading-relaxed body-copy">
              {askDonnyPrompt
                ? t("ecosystem.donny_try", { prompt: askDonnyPrompt })
                : t("ecosystem.donny_default_body")}
            </p>
          </div>
          <Link
            to={askDonnyHref}
            className="btn-primary text-sm inline-flex items-center gap-2 whitespace-nowrap self-start md:self-auto"
            data-testid={`${testidPrefix}-donny-cta`}
          >
            <Sparkles className="w-4 h-4" /> {t("nav.ask_donny")}
          </Link>
        </div>
      </Section>
    </>
  );
}
